import React, { forwardRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import {
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import { TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Container, Button, Card } from "react-bootstrap";
import axios from "../Components/Axios/Axios";
// Custom Components Import
import Loader from "../Components/Spinner/Spinner";
import Sidebar from "../Components/Sidebar/MainSidebar/Sidebar";
import requests from "../Components/Requests/Requests";
import { InputAdornment } from "@material-ui/core";
import { AccountCircle } from "@material-ui/icons";

const useStyles = makeStyles(() => ({
  noBorder: {
    border: "none",
    borderRadius: "5px",
  },
}));

// ---- Rows-per-page choices (kept in sync with the backend's
// ALLOWED_PAGE_SIZES whitelist in getusers) ----
const PAGE_SIZE_OPTIONS = [30, 60, 90, 120];

function AllBlogs() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0); // MaterialTable uses 0-based page indexing
  const [pageSize, setPageSize] = useState(30); // rows per page, one of PAGE_SIZE_OPTIONS
  const [totalCount, setTotalCount] = useState(0); // Total count of entries
  const [searchValue, setSearchValue] = useState("");

  // ---- Export all state ----
  const [exportingAll, setExportingAll] = useState(false);

  // ---- Filter state ----
  const [filters, setFilters] = useState({
    country: "",
    platform: "",
    fromDate: "",
    toDate: "",
    productId: "",
  });

  // ---- Filter dropdown options (from API) ----
  const [countryOptions, setCountryOptions] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [productIdOptions, setProductIdOptions] = useState([]);

  const tableIcons = {
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  useEffect(() => {
    // Fetch data whenever the page or rows-per-page changes
    if (searchValue === "") {
      fetchData(page + 1, filters); // Fetch data for the current page, respecting active filters
    } else {
      fetchDataSearch(searchValue, page + 1); // Fetch search data for the current page
    }
  }, [page, pageSize]); // Triggered when page or pageSize changes

  // ---- Fetch data, now accepts filters ----
  const fetchData = async (currentPage, currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await axios.get(requests.allUsers, {
        params: {
          page: currentPage,
          pageSize: pageSize,
          country: currentFilters.country,
          platform: currentFilters.platform,
          fromDate: currentFilters.fromDate,
          toDate: currentFilters.toDate,
          productId: currentFilters.productId,
        },
      });
      if (response.data.Success === 1) {
        setData(response.data.data); // Set new data
        setTotalCount(response.data.totalCount); // Set total count

        // ---- Populate filter dropdown options ----
        if (response.data.filterOptions) {
          setCountryOptions(response.data.filterOptions.countries || []);
          setPlatformOptions(response.data.filterOptions.platforms || []);
          setProductIdOptions(response.data.filterOptions.productIds || []);
        }
      } else {
        toast.warning(`Something went wrong`, {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDataSearch = async (searchValue, currentPage) => {
    setLoading(true);
    try {
      const response = await axios.get(requests.filterUsers, {
        params: { page: currentPage, search: searchValue }, // Pass 1-based page number and search term to the server
      });
      if (response.data.Success === 1) {
        setData(response.data.data); // Set new data
        setTotalCount(response.data.totalCount); // Set total count
      } else {
        toast.warning(`Something went wrong`, {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update current page
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // rows-per-page changed, go back to page 1
  };

  // ---- CSV helpers ----
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Plain-text versions of the render() cell helpers above, for CSV export
  const getCellValue = (row, col) => {
    if (col.field === "subscription_status") {
      return row.subscription_status === "0" || row.subscription_status === 0
        ? "Not Subscribed"
        : row.subscription_status;
    }
    if (col.field === "last_separation") {
      return renderLastSeparationText(row.last_separation);
    }
    return row[col.field];
  };

  const renderLastSeparationText = (value) => {
    if (!value || value === 0) return "";
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleString();
    } catch {
      return value;
    }
  };

  // ---- Export ALL filtered records ----
  // Hit allUsers once WITHOUT a `page` param -- the backend treats a missing
  // page as "return every matching row, unpaginated" (see isPaginated in
  // getusers). One request, one DB query, then just convert to CSV.
  const exportAllData = async (columnList) => {
    if (exportingAll) return;
    setExportingAll(true);

    const params = {
      country: filters.country,
      platform: filters.platform,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      productId: filters.productId,
    };

    try {
      const response = await axios.get(requests.allUsers, { params });

      if (response.data.Success !== 1) {
        toast.warning(
          response.data.message || "Export failed. Could not fetch records.",
          {
            position: "top-right",
            autoClose: 3500,
          },
        );
        return;
      }

      const rows = response.data.data || [];

      if (rows.length === 0) {
        toast.info("No records match these filters.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      // Only columns that map to real data get exported; action/button
      // columns (Edit Credits, etc.) are skipped since they have no field.
      const exportableCols = (columnList || []).filter(
        (col) => col.field && !col.exportSkip,
      );
      const headerLine = exportableCols
        .map((col) => csvEscape(col.title))
        .join(",");

      const lines = [headerLine];
      for (const row of rows) {
        const line = exportableCols
          .map((col) => csvEscape(getCellValue(row, col)))
          .join(",");
        lines.push(line);
      }
      const totalRows = rows.length;

      // single join, BOM so Excel reads UTF-8 correctly
      const blob = new Blob(["﻿" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `all_users_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${totalRows} records`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setExportingAll(false);
    }
  };

  const handleSearch = () => {
    // Trigger the search API call when the button is clicked
    setPage(0); // Reset to the first page
    if (searchValue === "") {
      fetchData(1, filters); // Fetch data for the current page
    } else {
      fetchDataSearch(searchValue, 1); // Fetch search data for the current page
    }
  };

  // ---- Filter handlers ----
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setPage(0); // reset to first page when filters change
    fetchData(1, filters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      country: "",
      platform: "",
      fromDate: "",
      toDate: "",
      productId: "",
    };
    setFilters(emptyFilters);
    setPage(0);
    fetchData(1, emptyFilters);
  };

  // ---- Helper to render subscription_status nicely ----
  const renderSubscriptionStatus = (value) => {
    if (value === "0" || value === 0) {
      return <span style={{ color: "#999" }}>Not Subscribed</span>;
    }
    return (
      <span style={{ color: "#2e7d32", fontWeight: "bold" }}>{value}</span>
    );
  };

  // ---- Helper to render product_id nicely ----
  const renderProductId = (value) => {
    if (value === 0 || value === "0") {
      return <span style={{ color: "#999" }}>--</span>;
    }
    return <span>{value}</span>;
  };

  // ---- Helper to render coins_status nicely ----
  const renderCoinsStatus = (value) => {
    if (value === 0 || value === "0") {
      return <span style={{ color: "#999" }}>--</span>;
    }
    return <span>{value}</span>;
  };

  // ---- Helper to format last_separation (epoch ms or date string) ----
  const renderLastSeparation = (value) => {
    if (!value || value === 0) {
      return <span style={{ color: "#999" }}>--</span>;
    }
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value; // fallback if already formatted string
      return date.toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <>
      <Sidebar />
      <Container className="content">
        <ToastContainer
          position="top-right"
          autoClose={10000}
          hideProgressBar={false}
        />
        <Card className="card">
          <Card.Header className="heading">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              className="inside-header"
            >
              <span>All Users</span>
              {/* <div className="searchbarUser">
                <TextField
                  variant="outlined"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  autoFocus
                  classes={{ notchedOutline: classes.input }}
                  className={classes.textField}
                  InputProps={{
                    classes: { notchedOutline: classes.noBorder },
                  }}
                  style={{
                    marginRight: "0px",
                    padding: "0px",
                    border: "1px solid #d2d2d2",
                  }}
                />
                <Button onClick={handleSearch} variant="primary">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                  >
                    <g
                      id="Group_40"
                      data-name="Group 40"
                      transform="translate(-0.001)"
                    >
                      <g
                        id="layer1_00000072963618680574873660000015406673454742146451_"
                        transform="translate(0.001)"
                      >
                        <g id="g18115">
                          <path
                            id="path17034"
                            d="M9.743,292.644A7.748,7.748,0,1,0,14.5,306.5l4.869,4.869a.95.95,0,0,0,1.343-1.343l-4.869-4.869a7.739,7.739,0,0,0-6.1-12.51Zm0,1.9a5.849,5.849,0,1,1-5.849,5.849A5.835,5.835,0,0,1,9.743,294.544Z"
                            transform="translate(-1.995 -292.644)"
                            fill="#B95BD0"
                          ></path>
                        </g>
                      </g>
                    </g>
                  </svg>
                </Button>
              </div> */}
            </div>
          </Card.Header>
          <Card.Body className="body">
            {/* ---- Filter Bar ---- */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <TextField
                select
                label="Country"
                SelectProps={{ native: true }}
                value={filters.country}
                onChange={(e) => handleFilterChange("country", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {countryOptions.map((c, i) => (
                  <option key={i} value={c}>
                    {c}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Platform"
                SelectProps={{ native: true }}
                value={filters.platform}
                onChange={(e) => handleFilterChange("platform", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {platformOptions.map((p, i) => (
                  <option key={i} value={p}>
                    {p}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Product ID"
                SelectProps={{ native: true }}
                value={filters.productId}
                onChange={(e) =>
                  handleFilterChange("productId", e.target.value)
                }
                style={{ minWidth: 160 }}
              >
                <option value=""></option>
                {productIdOptions.map((p, i) => (
                  <option key={i} value={p}>
                    {p}
                  </option>
                ))}
              </TextField>

              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              />

              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
              />

              <Button variant="primary" size="sm" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear
              </Button>

              {exportingAll && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                  }}
                >
                  <span className="mini-spinner" />
                  Exporting...
                </span>
              )}

              <span style={{ marginLeft: "auto", fontSize: "14px" }}>
                Total Results: {totalCount}
              </span>
            </div>

            {loading ? (
              <Loader margin={"auto"} />
            ) : (
              <MaterialTable
                data={data} // Data to be displayed in the table
                icons={tableIcons}
                columns={[
                  { title: "#", field: "id", type: "numeric" },
                  { title: "credits", field: "credits", type: "numeric" },
                  { title: "premium_credits", field: "premium_credits" },
                  { title: "email", field: "email_fixed" },
                  { title: "country_code", field: "country_code" },
                  { title: "platform", field: "platform" },
                  {
                    title: "register_date",
                    field: "formatted_register_date",
                  },
                  {
                    title: "subscription_status",
                    field: "subscription_status",
                    render: (rowData) =>
                      renderSubscriptionStatus(rowData.subscription_status),
                  },
                  {
                    title: "product_id",
                    field: "product_id",
                    render: (rowData) => renderProductId(rowData.product_id),
                  },
                  {
                    title: "coins_status",
                    field: "coins_status",
                    render: (rowData) =>
                      renderCoinsStatus(rowData.coins_status),
                  },
                  {
                    title: "last_separation",
                    field: "last_separation",
                    render: (rowData) =>
                      renderLastSeparation(rowData.last_separation),
                  },
                  {
                    title: "Edit Credits",
                    render: (rowData) => (
                      <Link to={{ pathname: "/editCredits", state: rowData }}>
                        <Button variant="primary" size="md">
                          Edit
                        </Button>
                      </Link>
                    ),
                  },
                ]}
                options={{
                  pageSize: pageSize, // Set default rows per page
                  paginationType: "normal", // Use a stepped pagination style
                  showTitle: false,
                  pagination: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  tableLayout: "auto",
                  search: false,
                  exportButton: true,
                  exportAllData: true,
                  // Override the built-in CSV export: renderData is only the
                  // current page, so we refetch every filtered record instead.
                  exportCsv: (columnList, renderData) => {
                    exportAllData(columnList);
                  },
                  rowStyle: (rowData, index) => ({
                    backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff", // Alternate colors
                  }),
                }}
                totalCount={totalCount} // Total number of rows (from backend)
                page={page} // Current page number (state)
                onChangePage={(newPage) => handlePageChange(newPage)} // Handle page change event
                onChangeRowsPerPage={(newPageSize) =>
                  handlePageSizeChange(newPageSize)
                }
                localization={{
                  pagination: {
                    labelRowsSelect: "rows",
                    labelDisplayedRows: "{from}-{to} of {count}",
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default AllBlogs;
